import { 
  Home, 
  Building2, 
  Table, 
  Network,
  Flame, 
  LogOut,
  ChevronLeft,
  ChevronRight
} from 'lucide-preact';
import { hashFromRoute, ROUTE_META, type AppRoute } from '../routing';

interface SidebarProps {
  collapsed: boolean;
  mobileOpen?: boolean;
  onToggle: () => void;
  currentPage: AppRoute;
  onPageChange: (page: AppRoute) => void;
  onLogout: () => void;
}

export const Sidebar = ({ collapsed, mobileOpen = false, onToggle, currentPage, onPageChange, onLogout }: SidebarProps) => {
  const overviewLabel = ROUTE_META.overview.title;
  const unitsLabel = ROUTE_META['unit-overview'].title;
  const financialStatementsLabel = ROUTE_META['financial-statements'].title;
  const fractalSwarmLabel = ROUTE_META['geospatial-intelligence'].title;
  const comingSoonLabel = ROUTE_META['coming-soon'].title;

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <div className="logo-container">
          {collapsed ? (
            <img src="/images/LOGO_NO_TXT.png" alt="Logo" className="logo-icon" />
          ) : (
            <img src="/images/LOGO.png" alt="Logo" className="logo-full" />
          )}
        </div>
      </div>
      
      <nav className="sidebar-nav">
        {collapsed ? (
          <>
            <a
              href={hashFromRoute('overview')}
              className={`nav-item ${currentPage === 'overview' ? 'active' : ''}`}
              title={overviewLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('overview');
              }}
            >
              <Home size={20} className="nav-icon" />
            </a>
            <a
              href={hashFromRoute('unit-overview')}
              className={`nav-item ${currentPage === 'unit-overview' ? 'active' : ''}`}
              title={unitsLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('unit-overview');
              }}
            >
              <Building2 size={20} className="nav-icon" />
            </a>
            <a
              href={hashFromRoute('financial-statements')}
              className={`nav-item ${currentPage === 'financial-statements' ? 'active' : ''}`}
              title={financialStatementsLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('financial-statements');
              }}
            >
              <Table size={20} className="nav-icon" />
            </a>
            <a
              href={hashFromRoute('geospatial-intelligence')}
              className={`nav-item ${currentPage === 'geospatial-intelligence' ? 'active' : ''}`}
              title={fractalSwarmLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('geospatial-intelligence');
              }}
            >
              <Network size={20} className="nav-icon" />
            </a>
            <a
              href={hashFromRoute('coming-soon')}
              className={`nav-item ${currentPage === 'coming-soon' ? 'active' : ''}`}
              title={comingSoonLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('coming-soon');
              }}
            >
              <Flame size={20} className="nav-icon" />
            </a>
          </>
        ) : (
          <>
            {/* Portfolio Section */}
            <div className="nav-section">
              <a
                href={hashFromRoute('overview')}
                className={`nav-item nav-section-header ${currentPage === 'overview' ? 'active' : ''}`}
                title={overviewLabel}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange('overview');
                }}
              >
                <Home size={20} className="nav-icon" />
                <span className="nav-text">{overviewLabel}</span>
              </a>
              <div className="nav-submenu">
                <a
                  href={hashFromRoute('unit-overview')}
                  className={`nav-item nav-subitem ${currentPage === 'unit-overview' ? 'active' : ''}`}
                  title={unitsLabel}
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange('unit-overview');
                  }}
                >
                  <Building2 size={18} className="nav-icon" />
                  <span className="nav-text">{unitsLabel}</span>
                </a>
                <a
                  href={hashFromRoute('financial-statements')}
                  className={`nav-item nav-subitem ${currentPage === 'financial-statements' ? 'active' : ''}`}
                  title={financialStatementsLabel}
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange('financial-statements');
                  }}
                >
                  <Table size={18} className="nav-icon" />
                  <span className="nav-text">{financialStatementsLabel}</span>
                </a>
              </div>
            </div>

            {/* Fractal Swarm (coverage map) */}
            <a
              href={hashFromRoute('geospatial-intelligence')}
              className={`nav-item ${currentPage === 'geospatial-intelligence' ? 'active' : ''}`}
              title={fractalSwarmLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('geospatial-intelligence');
              }}
            >
              <Network size={20} className="nav-icon" />
              <span className="nav-text">{fractalSwarmLabel}</span>
            </a>

            {/* In the Works */}
            <a
              href={hashFromRoute('coming-soon')}
              className={`nav-item ${currentPage === 'coming-soon' ? 'active' : ''}`}
              title={comingSoonLabel}
              onClick={(e) => {
                e.preventDefault();
                onPageChange('coming-soon');
              }}
            >
              <Flame size={20} className="nav-icon" />
              <span className="nav-text">{comingSoonLabel}</span>
            </a>
          </>
        )}

        {/* Logout */}
        <a href="#" className="nav-item logout" onClick={(e) => {
          e.preventDefault();
          onLogout();
        }} title={collapsed ? "Logout" : ""}>
          <LogOut size={20} className="nav-icon" />
          {!collapsed && <span className="nav-text">Logout</span>}
        </a>
      </nav>
      
      {/* Toggle Button at Bottom */}
      <div className="sidebar-footer">
        <a 
          href="#" 
          className="nav-item sidebar-toggle" 
          onClick={(e) => { e.preventDefault(); onToggle(); }} 
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={20} className="nav-icon" /> : <ChevronLeft size={20} className="nav-icon" />}
          {!collapsed && <span className="nav-text">Collapse</span>}
        </a>
      </div>
    </div>
  );
};
